import React, { Component } from 'react';
import CameraCapture from './../../common/CameraCapture';
import Tesseract from 'tesseract.js';

// US State abbreviations for license plate dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
];

// ============================================================================
// IMAGE COMPRESSION HELPER
// ============================================================================
/**
 * Compresses an image to reduce file size before upload
 * @param {string} dataUrl - Base64 data URL of the image
 * @param {number} maxWidth - Maximum width (default 1920)
 * @param {number} maxHeight - Maximum height (default 1080)
 * @param {number} quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<string>} Compressed image as data URL
 */
const compressImage = (dataUrl, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG with compression
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

      console.log(`🗜️ Image compressed: ${(dataUrl.length / 1024).toFixed(0)}KB -> ${(compressedDataUrl.length / 1024).toFixed(0)}KB`);

      resolve(compressedDataUrl);
    };

    img.onerror = (error) => {
      console.error('Error loading image for compression:', error);
      reject(error);
    };

    img.src = dataUrl;
  });
};

class PhotosOdometerStep extends Component {
  constructor(props) {
    super(props);

    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Get default state from company/installer if available
    // Uses installer_state field (2-char state abbreviation, e.g., "TX")
    const company = props.company;
    const defaultState = company?.installer_state || '';

    this.state = {
      isMobile: isMobile,

      // Odometer
      odometerPhoto: null,
      odometerReading: '',

      // License Plate (split into state and number)
      plateState: defaultState ? defaultState.toUpperCase() : '',
      plateNumber: '',

      // Condition photos (1-4)
      conditionPhotos: [],

      // Camera state
      showCamera: false,
      cameraType: null, // 'odometer' or 'condition'

      // UI state
      loading: false,
      error: null,
      ocrProcessing: false
    };

    this.handleOdometerReadingChange = this.handleOdometerReadingChange.bind(this);
    this.handleOdometerPhotoCapture = this.handleOdometerPhotoCapture.bind(this);
    this.handleConditionPhotoCapture = this.handleConditionPhotoCapture.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
  }

  // ========================================================================
  // ODOMETER HANDLERS
  // ========================================================================

  handleOdometerReadingChange(event) {
    this.setState({ odometerReading: event.target.value });
  }

  handlePlateStateChange = (event) => {
    this.setState({ plateState: event.target.value });
  }

  handlePlateNumberChange = (event) => {
    this.setState({ plateNumber: event.target.value.toUpperCase() });
  }

  async handleOdometerPhotoCapture(photoData) {
    console.log("📸 Odometer photo captured");

    try {
      // Compress the image before storing
      const compressedPreview = await compressImage(photoData.preview);

      const compressedPhotoData = {
        ...photoData,
        preview: compressedPreview
      };

      this.setState({
        odometerPhoto: compressedPhotoData,
        showCamera: false,
        cameraType: null,
        ocrProcessing: true
      });

      // Perform OCR on the odometer image (use original for better accuracy)
      console.log("🔍 Starting OCR processing...");

      const { data: { text } } = await Tesseract.recognize(
        photoData.preview, // Use original for OCR
        'eng',
        {
          logger: m => console.log(m) // Log progress
        }
      );

      console.log("📝 OCR Raw Text:", text);

      // Extract numbers from the text
      // Look for sequences of digits (odometer readings are typically 5-7 digits)
      const numbers = text.match(/\d{4,7}/g);

      if (numbers && numbers.length > 0) {
        // Take the longest number found (likely the odometer reading)
        const longestNumber = numbers.reduce((a, b) => a.length >= b.length ? a : b);
        console.log("✅ Detected odometer reading:", longestNumber);

        this.setState({
          odometerReading: longestNumber,
          ocrProcessing: false
        });
      } else {
        console.log("⚠️ No odometer reading detected");
        this.setState({ ocrProcessing: false });
      }
    } catch (error) {
      console.error("❌ Error processing odometer photo:", error);
      this.setState({ ocrProcessing: false });
    }
  }

  openOdometerCamera = () => {
    if (this.state.isMobile) {
      this.setState({ 
        showCamera: true,
        cameraType: 'odometer'
      });
    }
  };

  // ========================================================================
  // CONDITION PHOTO HANDLERS
  // ========================================================================

  async handleConditionPhotoCapture(photoData) {
    console.log("📸 Condition photo captured");

    try {
      // Compress the image before storing
      const compressedPreview = await compressImage(photoData.preview);

      const compressedPhotoData = {
        ...photoData,
        preview: compressedPreview
      };

      const updatedPhotos = [...this.state.conditionPhotos, compressedPhotoData];

      this.setState({
        conditionPhotos: updatedPhotos,
        showCamera: false,
        cameraType: null
      });
    } catch (error) {
      console.error("❌ Error compressing condition photo:", error);
      // Still add the photo if compression fails
      const updatedPhotos = [...this.state.conditionPhotos, photoData];
      this.setState({
        conditionPhotos: updatedPhotos,
        showCamera: false,
        cameraType: null
      });
    }
  }

  openConditionCamera = () => {
    if (this.state.conditionPhotos.length >= 4) {
      alert('Maximum 4 condition photos allowed');
      return;
    }

    if (this.state.isMobile) {
      this.setState({ 
        showCamera: true,
        cameraType: 'condition'
      });
    }
  };

  removeConditionPhoto = (index) => {
    const updatedPhotos = this.state.conditionPhotos.filter((_, i) => i !== index);
    this.setState({ conditionPhotos: updatedPhotos });
  };

  // ========================================================================
  // FILE UPLOAD HANDLER (Desktop)
  // ========================================================================

  async handleFileUpload(event, type) {
    const files = event.target.files;

    if (!files || files.length === 0) return;

    if (type === 'odometer') {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = async (e) => {
        const preview = e.target.result;

        try {
          // Compress the uploaded image
          const compressedPreview = await compressImage(preview);

          this.setState({
            odometerPhoto: {
              file: file,
              preview: compressedPreview
            },
            ocrProcessing: true
          });

          // Perform OCR on uploaded odometer image (use original for better accuracy)
          console.log("🔍 Starting OCR processing on uploaded image...");

          const { data: { text } } = await Tesseract.recognize(
            preview, // Use original for OCR
            'eng',
            {
              logger: m => console.log(m)
            }
          );

          console.log("📝 OCR Raw Text:", text);

          // Extract numbers from the text
          const numbers = text.match(/\d{4,7}/g);

          if (numbers && numbers.length > 0) {
            const longestNumber = numbers.reduce((a, b) => a.length >= b.length ? a : b);
            console.log("✅ Detected odometer reading:", longestNumber);

            this.setState({
              odometerReading: longestNumber,
              ocrProcessing: false
            });
          } else {
            console.log("⚠️ No odometer reading detected");
            this.setState({ ocrProcessing: false });
          }
        } catch (error) {
          console.error("❌ Error processing odometer photo:", error);
          this.setState({ ocrProcessing: false });
        }
      };

      reader.readAsDataURL(file);
      
    } else if (type === 'condition') {
      Array.from(files).forEach(file => {
        if (this.state.conditionPhotos.length >= 4) {
          alert('Maximum 4 condition photos allowed');
          return;
        }

        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            // Compress the uploaded image
            const compressedPreview = await compressImage(e.target.result);

            this.setState(prevState => ({
              conditionPhotos: [...prevState.conditionPhotos, {
                file: file,
                preview: compressedPreview
              }]
            }));
          } catch (error) {
            console.error("❌ Error compressing condition photo:", error);
            // Still add the photo if compression fails
            this.setState(prevState => ({
              conditionPhotos: [...prevState.conditionPhotos, {
                file: file,
                preview: e.target.result
              }]
            }));
          }
        };

        reader.readAsDataURL(file);
      });
    }
  }

  // ========================================================================
  // CONTINUE (No upload yet - just validation)
  // ========================================================================

  handleContinue() {
    // Validate required fields for CarFax integration
    if (!this.state.odometerReading || this.state.odometerReading.trim() === '') {
      this.setState({ error: 'Odometer reading is required for CarFax integration.' });
      return;
    }

    if (!this.state.plateState) {
      this.setState({ error: 'License plate state is required for CarFax integration.' });
      return;
    }

    // Plate number is optional (may not be available for dealer pre-sale vehicles)

    // Clear any previous errors
    this.setState({ error: null });

    console.log("✅ Photos captured locally - will upload after final confirmation");

    // Combine state and plate number for storage (plate number optional)
    const plateNumber = this.state.plateNumber?.trim();
    const fullPlate = plateNumber
      ? `${this.state.plateState}-${plateNumber}`
      : this.state.plateState;

    // Notify parent with photo data and plate (parent will handle upload later)
    this.props.onPhotosUploaded(
      this.state.odometerPhoto,
      this.state.odometerReading,
      this.state.conditionPhotos,
      fullPlate
    );
  }

  // ========================================================================
  // RENDER
  // ========================================================================

  render() {
    // Camera Modal (Mobile)
    if (this.state.showCamera) {
      return (
        <CameraCapture
          onCapture={
            this.state.cameraType === 'odometer' 
              ? this.handleOdometerPhotoCapture 
              : this.handleConditionPhotoCapture
          }
          onClose={() => this.setState({ showCamera: false, cameraType: null })}
        />
      );
    }

    return (
      <div className="photos-odometer-step">
        <h4 className="text-center mb-4">Step 2: Document Vehicle</h4>

        {/* Error Display */}
        {this.state.error && (
          <div className="alert alert-danger" role="alert">
            <i className="fa fa-exclamation-triangle mr-2"></i>
            {this.state.error}
          </div>
        )}

        {/* Odometer Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fa fa-tachometer mr-2"></i>
                  Odometer Reading
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* Odometer Reading Input */}
                  <div className="col-md-6">
                    <div className="form-group">
                      <label htmlFor="odometer-reading" className="font-weight-bold">
                        Odometer Reading <span className="text-danger">*</span>
                      </label>
                      <input
                        id="odometer-reading"
                        type="number"
                        className={`form-control ${!this.state.odometerReading ? 'border-warning' : ''}`}
                        value={this.state.odometerReading}
                        onChange={this.handleOdometerReadingChange}
                        placeholder="e.g., 45230"
                        disabled={this.state.loading || this.state.ocrProcessing}
                        required
                      />
                      {this.state.ocrProcessing && (
                        <small className="text-info mt-2 d-block">
                          <i className="fa fa-spinner fa-spin mr-1"></i>
                          Reading odometer from photo...
                        </small>
                      )}
                      <small className="text-muted">Required for CarFax integration</small>
                    </div>
                  </div>

                  {/* Odometer Photo */}
                  <div className="col-md-6">
                    <label className="font-weight-bold">Odometer Photo (optional)</label>
                    
                    {!this.state.odometerPhoto ? (
                      // Upload/Capture Button
                      <div>
                        {this.state.isMobile ? (
                          <button
                            className="btn btn-primary btn-block"
                            onClick={this.openOdometerCamera}
                            disabled={this.state.loading}
                          >
                            <i className="fa fa-camera mr-2"></i>
                            Take Photo
                          </button>
                        ) : (
                          <div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => this.handleFileUpload(e, 'odometer')}
                              className="d-none"
                              id="odometer-file-input"
                              disabled={this.state.loading}
                            />
                            <label 
                              htmlFor="odometer-file-input" 
                              className="btn btn-primary btn-block mb-0"
                              style={{ cursor: 'pointer' }}
                            >
                              <i className="fa fa-upload mr-2"></i>
                              Upload Photo
                            </label>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Preview
                      <div className="position-relative">
                        <img 
                          src={this.state.odometerPhoto.preview} 
                          alt="Odometer" 
                          className="img-fluid border rounded"
                          style={{ maxHeight: '200px', width: '100%', objectFit: 'cover' }}
                        />
                        <button
                          className="btn btn-danger btn-sm position-absolute"
                          style={{ top: '5px', right: '5px' }}
                          onClick={() => this.setState({ odometerPhoto: null })}
                          disabled={this.state.loading}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* License Plate Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fa fa-car mr-2"></i>
                  License Plate
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* State Dropdown */}
                  <div className="col-md-3 col-4">
                    <div className="form-group mb-0">
                      <label htmlFor="plateState" className="font-weight-bold">
                        State <span className="text-danger">*</span>
                      </label>
                      <select
                        id="plateState"
                        className={`form-control ${!this.state.plateState ? 'border-warning' : ''}`}
                        value={this.state.plateState}
                        onChange={this.handlePlateStateChange}
                        disabled={this.state.loading}
                        required
                      >
                        <option value="">Select</option>
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Plate Number */}
                  <div className="col-md-5 col-8">
                    <div className="form-group mb-0">
                      <label htmlFor="plateNumber" className="font-weight-bold">
                        Plate Number <span className="text-muted font-weight-normal">(optional)</span>
                      </label>
                      <input
                        id="plateNumber"
                        type="text"
                        className="form-control"
                        value={this.state.plateNumber}
                        onChange={this.handlePlateNumberChange}
                        placeholder="e.g., ABC1234"
                        disabled={this.state.loading}
                        maxLength={10}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                  </div>
                </div>
                <small className="text-muted mt-2 d-block">State is required for CarFax integration</small>
              </div>
            </div>
          </div>
        </div>

        {/* Condition Photos Section */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fa fa-camera mr-2"></i>
                  Condition Photos (optional)
                  <span className="badge badge-light ml-2">
                    {this.state.conditionPhotos.length}/4
                  </span>
                </h5>
              </div>
              <div className="card-body">
                <p className="text-muted mb-3">
                  Optionally take up to 4 photos showing the vehicle's current condition
                </p>

                {/* Photo Grid */}
                <div className="row">
                  {this.state.conditionPhotos.map((photo, index) => (
                    <div key={index} className="col-6 col-md-3 mb-3">
                      <div className="position-relative">
                        <img 
                          src={photo.preview} 
                          alt={`Condition ${index + 1}`} 
                          className="img-fluid border rounded"
                          style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                        />
                        <button
                          className="btn btn-danger btn-sm position-absolute"
                          style={{ top: '5px', right: '5px' }}
                          onClick={() => this.removeConditionPhoto(index)}
                          disabled={this.state.loading}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Add Photo Button */}
                  {this.state.conditionPhotos.length < 4 && (
                    <div className="col-6 col-md-3 mb-3">
                      {this.state.isMobile ? (
                        <button
                          className="btn btn-outline-primary btn-block"
                          onClick={this.openConditionCamera}
                          disabled={this.state.loading}
                          style={{ height: '150px' }}
                        >
                          <i className="fa fa-camera fa-2x mb-2"></i>
                          <br />
                          Add Photo
                        </button>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => this.handleFileUpload(e, 'condition')}
                            className="d-none"
                            id="condition-file-input"
                            disabled={this.state.loading}
                          />
                          <label 
                            htmlFor="condition-file-input" 
                            className="btn btn-outline-primary btn-block mb-0"
                            style={{ height: '150px', cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                          >
                            <i className="fa fa-upload fa-2x mb-2"></i>
                            <br />
                            Upload Photo
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Continue Button */}
        <div className="row mt-4">
          <div className="col-12 text-center">
            <button
              className="btn btn-primary btn-lg"
              onClick={this.handleContinue}
              disabled={this.state.loading}
            >
              {this.state.loading && (
                <span className="mr-2">
                  <i className="fa fa-refresh fa-spin" />
                </span>
              )}
              CONTINUE
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default PhotosOdometerStep;
